import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BanknotesIcon,
  TrophyIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import Button from '../../components/common/Button';

interface AgentSummary {
  id: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface BuyerSummary {
  id: string;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

interface CommissionRow {
  id: string;
  orderNumber: string;
  createdAt: string;
  network: string;
  orderStatus: string;
  paymentStatus: string;
  derivedStatus: 'available' | 'pending' | string;
  sellingAmount: number;
  baseTotalPrice: number;
  commissionAmount: number;
  storefront?: {
    storeId?: string;
    storeUrl?: string;
  };
  agent?: AgentSummary;
  buyer?: BuyerSummary;
}

interface OverviewResponse {
  stats: {
    totalOrders: number;
    totalAmount: number;
    avgAmount: number;
    pendingAmount: number;
    availableAmount: number;
    lastOrderDate?: string | null;
  };
  statusBreakdown: Array<{ _id: string; count: number; totalAmount: number; avgAmount: number }>;
  topAgents: Array<{
    agentId: string;
    totalOrders: number;
    totalAmount: number;
    lastOrderDate?: string;
    agent?: any;
  }>;
  recentOrders: Array<any>;
  period: string;
  filters: { status: string | null; agentId: string | null };
}

interface ListResponse {
  commissions: CommissionRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  period: string;
  filters: any;
}

interface DetailResponse {
  commission: {
    id: string;
    orderNumber: string;
    createdAt: string;
    network: string;
    orderStatus: string;
    paymentStatus: string;
    sellingAmount: number;
    baseTotalPrice: number;
    commissionAmount: number;
    derivedStatus: string;
    storefront: {
      storeId: string | null;
      storeUrl: string | null;
      storeAgentId: string;
    };
    buyer: any;
    agent: any;
  };
}

interface PayoutAgent {
  _id: string;
  email?: string;
  username?: string;
  phone?: string;
  role?: string;
}

interface PayoutRow {
  id: string;
  createdAt: string;
  amount: number;
  method: string;
  status: string;
  reference: string;
  externalReference?: string | null;
  agent?: PayoutAgent | null;
  phoneNumberMasked?: string | null;
  mobileNetwork?: string | null;
  notes?: string | null;
}

interface PayoutListResponse {
  payouts: PayoutRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  filters: { status: string | null; search: string | null };
}

interface PerformanceAgentRow {
  agentId: string;
  agentName: string;
  email?: string;
  username?: string;
  role?: string;
  requestedRole?: string;
  totalOrders: number;
  totalSalesGenerated: number;
  totalCommissionEarned: number;
  totalCommissionApproved: number;
  totalCommissionPending: number;
  totalCommissionPaidOut: number;
  currentCommissionBalance: number;
}

interface PerformanceSummary {
  totalAgents: number;
  totalRevenueGeneratedViaAgents: number;
  totalCommissionEarned: number;
  totalCommissionPendingApproval: number;
  totalCommissionApproved: number;
  totalCommissionPaidOut: number;
  totalCommissionBalance: number;
}

interface PerformanceLeaderboardRow {
  agentId: string;
  agentName: string;
  email?: string;
  totalSalesGenerated: number;
  totalCommissionEarned: number;
  rank: number;
}

interface PerformanceResponse {
  summary: PerformanceSummary;
  leaderboard: PerformanceLeaderboardRow[];
  agents: PerformanceAgentRow[];
  pagination: { page: number; limit: number; total: number; pages: number };
  filters: {
    search: string | null;
    sortBy: string;
    sortOrder: string;
    range: string;
    startDate: string | null;
    endDate: string | null;
  };
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount || 0);

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GH');
};

const AdminCommissionManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'performance' | 'orders'>('performance');

  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [listData, setListData] = useState<ListResponse | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceResponse | null>(null);

  const [payoutsData, setPayoutsData] = useState<PayoutListResponse | null>(null);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [payoutsFilter, setPayoutsFilter] = useState({ status: 'pending', search: '' });
  const [processingPayoutId, setProcessingPayoutId] = useState<string | null>(null);
  const [processingCommissionId, setProcessingCommissionId] = useState<string | null>(null);

  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingPerformance, setLoadingPerformance] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [payoutsError, setPayoutsError] = useState<string | null>(null);

  const [filters, setFilters] = useState({
    period: 'all',
    status: 'all',
    agentId: '',
    search: '',
    minAmount: '',
    maxAmount: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const [performanceFilters, setPerformanceFilters] = useState({
    range: 'all',
    startDate: '',
    endDate: '',
    search: '',
    sortBy: 'revenue',
    sortOrder: 'desc'
  });

  const [page, setPage] = useState(1);
  const [performancePage, setPerformancePage] = useState(1);

  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const [showFilters, setShowFilters] = useState(false);

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', '50');

    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (filters.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters.agentId?.trim()) params.set('agentId', filters.agentId.trim());
    if (filters.search?.trim()) params.set('search', filters.search.trim());
    if (filters.minAmount?.trim()) params.set('minAmount', filters.minAmount.trim());
    if (filters.maxAmount?.trim()) params.set('maxAmount', filters.maxAmount.trim());
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

    return params;
  }, [filters, page]);

  const performanceQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(performancePage));
    params.set('limit', '50');

    if (performanceFilters.range) params.set('range', performanceFilters.range);
    if (performanceFilters.search?.trim()) params.set('search', performanceFilters.search.trim());
    if (performanceFilters.sortBy) params.set('sortBy', performanceFilters.sortBy);
    if (performanceFilters.sortOrder) params.set('sortOrder', performanceFilters.sortOrder);

    if (performanceFilters.range === 'custom') {
      if (performanceFilters.startDate?.trim()) params.set('startDate', performanceFilters.startDate.trim());
      if (performanceFilters.endDate?.trim()) params.set('endDate', performanceFilters.endDate.trim());
    }

    return params;
  }, [performanceFilters, performancePage]);

  const fetchPerformance = async () => {
    try {
      setLoadingPerformance(true);
      setError(null);

      const response = await fetch(`/api/admin/commissions/performance?${performanceQueryParams}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load agent commission performance');

      const result = await response.json();
      setPerformanceData(result.data);
    } catch (e) {
      setPerformanceData(null);
      const msg = e instanceof Error ? e.message : 'Failed to load performance data';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const approveCommission = async (row: CommissionRow) => {
    const postApprove = async (body: any) => {
      const response = await fetch(`/api/admin/commissions/${row.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body || {})
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        const err: any = new Error(payload?.message || 'Failed to approve commission');
        err.payload = payload;
        throw err;
      }

      return payload;
    };

    try {
      const ok = window.confirm('Manually approve this pending commission by re-verifying the underlying payment?');
      if (!ok) return;

      setProcessingCommissionId(row.id);

      let payload: any;
      try {
        payload = await postApprove({});
      } catch (e: any) {
        const code = e?.payload?.code;

        if (code === 'MISSING_PAYSTACK_REFERENCE') {
          const ref = window.prompt('Enter Paystack reference');
          if (!ref || !String(ref).trim()) {
            toast.error('Paystack reference is required');
            return;
          }
          payload = await postApprove({ provider: 'paystack', paystackReference: String(ref).trim() });
        } else if (code === 'MISSING_LEDGER_IDENTIFIER') {
          const txId = window.prompt('Enter Mobile Money transaction ID');
          if (!txId || !String(txId).trim()) {
            toast.error('Mobile Money transaction ID is required');
            return;
          }
          payload = await postApprove({ provider: 'mobile_money', ledgerTransactionId: String(txId).trim() });
        } else if (code === 'UNKNOWN_PROVIDER') {
          const provider = window.prompt('Enter provider (paystack or mobile_money)');
          const normalized = String(provider || '').trim().toLowerCase();
          if (!['paystack', 'mobile_money'].includes(normalized)) {
            toast.error('Provider must be paystack or mobile_money');
            return;
          }
          payload = await postApprove({ provider: normalized });
        } else {
          throw e;
        }
      }

      toast.success(payload?.message || 'Commission approved');

      await Promise.allSettled([fetchOverview(), fetchList()]);
      if (detailOpen && detail?.commission?.id === row.id) {
        await fetchDetail(row.id);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to approve commission');
    } finally {
      setProcessingCommissionId(null);
    }
  };

  const fetchOverview = async () => {
    try {
      setLoadingOverview(true);
      setOverviewError(null);

      const params = new URLSearchParams();
      if (filters.period && filters.period !== 'all') params.set('period', filters.period);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.agentId?.trim()) params.set('agentId', filters.agentId.trim());

      const response = await fetch(`/api/admin/commissions/overview?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load commission overview');

      const result = await response.json();
      setOverview(result.data);
    } catch (e) {
      setOverview(null);
      const msg = e instanceof Error ? e.message : 'Failed to load overview';
      setOverviewError(msg);
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchList = async () => {
    try {
      setLoadingList(true);
      setListError(null);

      const response = await fetch(`/api/admin/commissions/list?${queryParams}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load commissions');

      const result = await response.json();
      setListData(result.data);
    } catch (e) {
      setListData(null);
      const msg = e instanceof Error ? e.message : 'Failed to load commissions';
      setListError(msg);
      toast.error(msg);
    } finally {
      setLoadingList(false);
    }
  };

  const payoutsQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(payoutsPage));
    params.set('limit', '50');
    if (payoutsFilter.status && payoutsFilter.status !== 'all') params.set('status', payoutsFilter.status);
    if (payoutsFilter.search?.trim()) params.set('search', payoutsFilter.search.trim());
    return params;
  }, [payoutsPage, payoutsFilter]);

  const fetchPayouts = async () => {
    try {
      setLoadingPayouts(true);
      setPayoutsError(null);

      const response = await fetch(`/api/admin/commissions/payouts?${payoutsQueryParams.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load payout requests');

      const result = await response.json();
      setPayoutsData(result.data);
    } catch (e) {
      setPayoutsData(null);
      const msg = e instanceof Error ? e.message : 'Failed to load payout requests';
      setPayoutsError(msg);
      toast.error(msg);
    } finally {
      setLoadingPayouts(false);
    }
  };

  const approvePayout = async (row: PayoutRow) => {
    try {
      setProcessingPayoutId(row.id);

      const body: any = {};
      if (row.method === 'mobile_money' && !row.externalReference) {
        const ext = window.prompt('Enter external reference (required for Mobile Money approvals)');
        if (!ext || !String(ext).trim()) {
          toast.error('External reference is required');
          return;
        }
        body.externalReference = String(ext).trim();
      }

      const response = await fetch(`/api/admin/commissions/payouts/${row.id}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to approve payout');
      }

      toast.success(payload?.message || 'Payout approved');
      await fetchPayouts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to approve payout');
    } finally {
      setProcessingPayoutId(null);
    }
  };

  const rejectPayout = async (row: PayoutRow) => {
    try {
      const ok = window.confirm('Reject this payout request?');
      if (!ok) return;

      setProcessingPayoutId(row.id);
      const response = await fetch(`/api/admin/commissions/payouts/${row.id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Failed to reject payout');
      }

      toast.success(payload?.message || 'Payout rejected');
      await fetchPayouts();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to reject payout');
    } finally {
      setProcessingPayoutId(null);
    }
  };

  const fetchOrdersTab = async () => {
    setLoadingOverview(true);
    setLoadingList(true);
    setOverviewError(null);
    setListError(null);

    await Promise.allSettled([fetchOverview(), fetchList()]);
  };

  const fetchDetail = async (commissionId: string) => {
    try {
      setDetailLoading(true);

      const response = await fetch(`/api/admin/commissions/${commissionId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load commission detail');

      const result = await response.json();
      setDetail(result.data);
    } catch (e) {
      setDetail(null);
      toast.error(e instanceof Error ? e.message : 'Failed to load commission detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = async (row: CommissionRow) => {
    setDetailOpen(true);
    await fetchDetail(row.id);
  };

  const onApply = () => {
    if (activeTab === 'orders') {
      setPage(1);
      fetchOrdersTab();
      return;
    }

    setPerformancePage(1);
    fetchPerformance();
  };

  const onReset = () => {
    if (activeTab === 'orders') {
      setFilters({
        period: 'all',
        status: 'all',
        agentId: '',
        search: '',
        minAmount: '',
        maxAmount: '',
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      setPage(1);
      setTimeout(() => {
        fetchOrdersTab();
      }, 0);
      return;
    }

    setPerformanceFilters({
      range: 'all',
      startDate: '',
      endDate: '',
      search: '',
      sortBy: 'revenue',
      sortOrder: 'desc'
    });
    setPerformancePage(1);
    setTimeout(() => {
      fetchPerformance();
    }, 0);
  };

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const params = new URLSearchParams();
      if (filters.period && filters.period !== 'all') params.set('period', filters.period);
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.agentId?.trim()) params.set('agentId', filters.agentId.trim());

      const response = await fetch(`/api/admin/commissions/export?format=${format}&${params}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to export');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissions-${filters.period}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    }
  };

  useEffect(() => {
    if (activeTab === 'performance') {
      fetchPerformance();
      return;
    }
    fetchOrdersTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab !== 'orders') return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()]);

  useEffect(() => {
    fetchPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payoutsQueryParams.toString()]);

  useEffect(() => {
    if (activeTab !== 'performance') return;
    fetchPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performanceQueryParams.toString()]);

  const pagination = activeTab === 'orders' ? listData?.pagination : performanceData?.pagination;
  const canPrev = (pagination?.page || 1) > 1;
  const canNext = (pagination?.page || 1) < (pagination?.pages || 1);

  const statusPill = (status: string) => {
    const s = String(status || '').toLowerCase();
    if (s === 'available') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
          <CheckCircleIcon className="h-4 w-4" />
          Available
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200">
        <ClockIcon className="h-4 w-4" />
        Pending
      </span>
    );
  };

  const isInitialLoading =
    (activeTab === 'orders' && (loadingOverview || loadingList) && !overview && !listData) ||
    (activeTab === 'performance' && loadingPerformance && !performanceData);

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Agent Commission Management</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Derived from storefront orders using <span className="font-mono">baseTotalPrice</span> and <span className="font-mono">metadata.storeAgentId</span>
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setActiveTab('performance');
              setShowFilters(false);
              if (!performanceData) fetchPerformance();
            }}
            variant={activeTab === 'performance' ? 'primary' : 'outline'}
          >
            Performance
          </Button>
          <Button
            onClick={() => {
              setActiveTab('orders');
              setShowFilters(false);
              if (!overview) fetchOverview();
              if (!listData) fetchList();
            }}
            variant={activeTab === 'orders' ? 'primary' : 'outline'}
          >
            Orders
          </Button>
        </div>

        {activeTab === 'orders' && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Rows: {(listData?.commissions || []).length} • Period: {filters.period}
          </div>
        )}
        {activeTab === 'performance' && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Agents: {(performanceData?.pagination?.total || 0).toLocaleString()} • Range: {performanceFilters.range}
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-200">
          {error}
        </div>
      )}

      {(overviewError || listError) && activeTab === 'orders' && (
        <div className="mb-6 rounded-lg border border-yellow-200 dark:border-yellow-900 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-900 dark:text-yellow-200">
          {overviewError ? `Overview: ${overviewError}` : null}
          {overviewError && listError ? ' • ' : null}
          {listError ? `List: ${listError}` : null}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowFilters((v) => !v)}
            variant="outline"
            className="flex items-center gap-2"
          >
            <FunnelIcon className="h-5 w-5" />
            Filters
          </Button>
          <Button
            onClick={onApply}
            className="flex items-center gap-2"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            Apply
          </Button>
          <Button
            onClick={onReset}
            variant="outline"
          >
            Reset
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'orders' && (
            <>
              <Button
                onClick={() => handleExport('csv')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Export CSV
              </Button>
              <Button
                onClick={() => handleExport('json')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Export JSON
              </Button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden"
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {activeTab === 'performance' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Range</label>
                      <select
                        value={performanceFilters.range}
                        onChange={(e) =>
                          setPerformanceFilters((p) => ({
                            ...p,
                            range: e.target.value,
                            ...(e.target.value !== 'custom' ? { startDate: '', endDate: '' } : {})
                          }))
                        }
                        className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      >
                        <option value="all">All time</option>
                        <option value="today">Today</option>
                        <option value="week">This week</option>
                        <option value="month">This month</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>

                    {performanceFilters.range === 'custom' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start date</label>
                          <input
                            type="date"
                            value={performanceFilters.startDate}
                            onChange={(e) => setPerformanceFilters((p) => ({ ...p, startDate: e.target.value }))}
                            className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End date</label>
                          <input
                            type="date"
                            value={performanceFilters.endDate}
                            onChange={(e) => setPerformanceFilters((p) => ({ ...p, endDate: e.target.value }))}
                            className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                          />
                        </div>
                      </>
                    )}

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        value={performanceFilters.search}
                        onChange={(e) => setPerformanceFilters((p) => ({ ...p, search: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        placeholder="Agent name, email, username, phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort</label>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={performanceFilters.sortBy}
                          onChange={(e) => setPerformanceFilters((p) => ({ ...p, sortBy: e.target.value }))}
                          className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        >
                          <option value="revenue">Revenue</option>
                          <option value="commission">Commission</option>
                          <option value="pending">Pending</option>
                          <option value="balance">Balance</option>
                          <option value="name">Name</option>
                        </select>
                        <select
                          value={performanceFilters.sortOrder}
                          onChange={(e) => setPerformanceFilters((p) => ({ ...p, sortOrder: e.target.value }))}
                          className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        >
                          <option value="desc">Desc</option>
                          <option value="asc">Asc</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === 'orders' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period</label>
                      <select
                        value={filters.period}
                        onChange={(e) => setFilters((p) => ({ ...p, period: e.target.value }))}
                        className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      >
                        <option value="all">All time</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="365d">Last 365 days</option>
                      </select>
                    </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  >
                    <option value="all">All</option>
                    <option value="available">Available</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Agent ID</label>
                  <input
                    value={filters.agentId}
                    onChange={(e) => setFilters((p) => ({ ...p, agentId: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    placeholder="Mongo ObjectId"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                  <input
                    value={filters.search}
                    onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    placeholder="Agent email/username, buyer email/username, or order number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort</label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={filters.sortBy}
                      onChange={(e) => setFilters((p) => ({ ...p, sortBy: e.target.value }))}
                      className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    >
                      <option value="createdAt">Created date</option>
                      <option value="commissionAmount">Commission amount</option>
                      <option value="orderAmount">Order amount</option>
                      <option value="status">Derived status</option>
                    </select>
                    <select
                      value={filters.sortOrder}
                      onChange={(e) => setFilters((p) => ({ ...p, sortOrder: e.target.value }))}
                      className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    >
                      <option value="desc">Desc</option>
                      <option value="asc">Asc</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Min commission</label>
                  <input
                    value={filters.minAmount}
                    onChange={(e) => setFilters((p) => ({ ...p, minAmount: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    placeholder="e.g. 1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max commission</label>
                  <input
                    value={filters.maxAmount}
                    onChange={(e) => setFilters((p) => ({ ...p, maxAmount: e.target.value }))}
                    className="w-full rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    placeholder="e.g. 100"
                  />
                </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {activeTab === 'orders' && (
        <>
          <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pending Payout Requests</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Approve or reject agent commission withdrawals</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={payoutsFilter.search}
                  onChange={(e) => {
                    setPayoutsPage(1);
                    setPayoutsFilter((p) => ({ ...p, search: e.target.value }));
                  }}
                  className="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="Search agent email/username/phone"
                />
                <select
                  value={payoutsFilter.status}
                  onChange={(e) => {
                    setPayoutsPage(1);
                    setPayoutsFilter((p) => ({ ...p, status: e.target.value }));
                  }}
                  className="rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                >
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All</option>
                </select>
                <Button variant="outline" onClick={fetchPayouts} disabled={loadingPayouts}>
                  Refresh
                </Button>
              </div>
            </div>

            {payoutsError && (
              <div className="mb-4 text-sm text-red-700 dark:text-red-200">{payoutsError}</div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Agent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Method</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(payoutsData?.payouts || []).map((p) => (
                    <tr key={p.id}>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        <div className="font-medium">{p.agent?.email || p.agent?.username || p.agent?._id || '—'}</div>
                        {p.phoneNumberMasked ? (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{p.phoneNumberMasked}{p.mobileNetwork ? ` • ${p.mobileNetwork}` : ''}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{p.method}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right">{formatCurrency(p.amount)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{p.status}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">{formatDateTime(p.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        {p.status === 'pending' || p.status === 'processing' ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              onClick={() => approvePayout(p)}
                              disabled={processingPayoutId === p.id}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => rejectPayout(p)}
                              disabled={processingPayoutId === p.id}
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {(payoutsData?.payouts || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                        {loadingPayouts ? 'Loading…' : 'No payout requests'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Commission</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(overview?.stats?.totalAmount || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Available</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(overview?.stats?.availableAmount || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(overview?.stats?.pendingAmount || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-8 w-8 text-gray-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Orders Count</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {(overview?.stats?.totalOrders || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Agent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Buyer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {(listData?.commissions || []).map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{row.orderNumber || row.id}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(row.createdAt)}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{row.network}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {row.agent?.email || row.agent?.username || row.agent?.id || '—'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {row.storefront?.storeUrl ? `/${row.storefront.storeUrl}` : row.storefront?.storeId || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {row.buyer?.email || row.buyer?.username || '—'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(row.commissionAmount)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Sell {formatCurrency(row.sellingAmount)} • Base {formatCurrency(row.baseTotalPrice)}
                        </div>
                      </td>
                      <td className="px-6 py-4">{statusPill(row.derivedStatus)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center justify-end gap-2">
                          {String(row.derivedStatus).toLowerCase() === 'pending' && (
                            <Button
                              onClick={() => approveCommission(row)}
                              disabled={processingCommissionId === row.id}
                              className="inline-flex items-center gap-2"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                              Approve
                            </Button>
                          )}

                          <Button
                            onClick={() => openDetail(row)}
                            variant="outline"
                            className="inline-flex items-center gap-2"
                          >
                            <EyeIcon className="h-5 w-5" />
                            View
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {(!listData?.commissions || listData.commissions.length === 0) && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                        No commission rows found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {pagination?.page || 1} of {pagination?.pages || 1} • Total {pagination?.total || 0}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => canPrev && setPage((p) => Math.max(1, p - 1))}
                  disabled={!canPrev}
                  className="inline-flex items-center gap-1"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                  Prev
                </Button>
                <Button
                  variant="outline"
                  onClick={() => canNext && setPage((p) => p + 1)}
                  disabled={!canNext}
                  className="inline-flex items-center gap-1"
                >
                  Next
                  <ChevronRightIcon className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'performance' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Revenue via Agents</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(performanceData?.summary?.totalRevenueGeneratedViaAgents || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Commission Earned</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(performanceData?.summary?.totalCommissionEarned || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-8 w-8 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Approval</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(performanceData?.summary?.totalCommissionPendingApproval || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Paid Out</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(performanceData?.summary?.totalCommissionPaidOut || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Top Agents</h2>
                <TrophyIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="space-y-3">
                {(performanceData?.leaderboard || []).slice(0, 10).map((row) => {
                  const top = row.rank <= 3;
                  return (
                    <div
                      key={row.agentId}
                      className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                        top
                          ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/20'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          #{row.rank} {row.agentName}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                          {row.email || row.agentId}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600 dark:text-gray-400">Revenue</div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(row.totalSalesGenerated)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {(!performanceData?.leaderboard || performanceData.leaderboard.length === 0) && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">No leaderboard data.</div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Agent</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Total Sales</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Commission Earned</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Approved</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Pending</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Paid Out</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {(performanceData?.agents || []).map((row) => (
                      <tr key={row.agentId} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{row.agentName}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.email || row.username || row.agentId}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                          {formatCurrency(row.totalSalesGenerated)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(row.totalCommissionEarned)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                          {formatCurrency(row.totalCommissionApproved)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                          {formatCurrency(row.totalCommissionPending)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm text-gray-900 dark:text-white">
                          {formatCurrency(row.totalCommissionPaidOut)}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(row.currentCommissionBalance)}
                        </td>
                      </tr>
                    ))}

                    {(!performanceData?.agents || performanceData.agents.length === 0) && (
                      <tr>
                        <td colSpan={7} className="px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                          No agent performance rows found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Page {pagination?.page || 1} of {pagination?.pages || 1} • Total {pagination?.total || 0}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => canPrev && setPerformancePage((p) => Math.max(1, p - 1))}
                    disabled={!canPrev}
                    className="inline-flex items-center gap-1"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => canNext && setPerformancePage((p) => p + 1)}
                    disabled={!canNext}
                    className="inline-flex items-center gap-1"
                  >
                    Next
                    <ChevronRightIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <Modal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        title={'Commission Detail'}
        size="xlarge"
      >
        {detailLoading && (
          <div className="flex items-center justify-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {!detailLoading && detail?.commission && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Order</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Order #</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.orderNumber}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Created</span><span className="font-medium text-gray-900 dark:text-white">{formatDateTime(detail.commission.createdAt)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Network</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.network}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Order Status</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.orderStatus}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Payment</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.paymentStatus}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Derived</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.derivedStatus}</span></div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Commission</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Selling</span><span className="font-medium text-gray-900 dark:text-white">{formatCurrency(detail.commission.sellingAmount)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Base Total</span><span className="font-medium text-gray-900 dark:text-white">{formatCurrency(detail.commission.baseTotalPrice)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Commission</span><span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(detail.commission.commissionAmount)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Store</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.storefront?.storeUrl || detail.commission.storefront?.storeId || '—'}</span></div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Agent</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Agent ID</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.storefront.storeAgentId}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.agent?.email || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Username</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.agent?.username || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.agent?.phone || '—'}</span></div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/40 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Buyer</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.buyer?.email || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Username</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.buyer?.username || '—'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Phone</span><span className="font-medium text-gray-900 dark:text-white">{detail.commission.buyer?.phone || '—'}</span></div>
              </div>
            </div>
          </div>
        )}

        {!detailLoading && !detail?.commission && (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            No detail available.
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminCommissionManagement;
